import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, StaggerGroup, StaggerChild } from "@/components/site/Reveal";
import { SOLUTIONS } from "@/data/content";

/* ------------------------------------------------------------------
   PRODUCTS — 4 rich product cards (Web · Voice · WhatsApp · RCS)
------------------------------------------------------------------- */
const PRODUCT_MESH = ["", "mesh-b", "mesh-c", "mesh-b"];

export const ProductShowcase = () => (
    <section className="py-24 md:py-28 bg-warm" data-testid="products-showcase">
        <div className="container-x">
            <Reveal>
                <div className="mb-14 max-w-3xl">
                    <div className="text-eyebrow text-slate-500 mb-3">Our Solutions</div>
                    <h2 className="heading-section text-3xl md:text-5xl">
                        <span className="text-slate-400">Purpose-built AI.</span> Three focused solutions.
                    </h2>
                    <p className="mt-5 text-intro text-slate-600 text-base md:text-lg">
                        The Nyra AI initiative delivers three focused solutions, each engineered to solve a
                        distinct enterprise challenge — and all built on the same secure, scalable AI foundation.
                    </p>
                </div>
            </Reveal>

            <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {SOLUTIONS.map((s, i) => (
                    <StaggerChild key={s.slug}>
                        <Link
                            to={`/solutions/${s.slug}`}
                            className="block h-full group"
                            data-testid={`product-card-${s.slug}`}
                        >
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                className="gleap-card h-full flex flex-col overflow-hidden"
                                style={{ padding: 0 }}
                            >
                                {/* Mesh header with icon */}
                                <div
                                    className={`mesh-frame ${PRODUCT_MESH[i]} flex items-center justify-center`}
                                    style={{
                                        borderRadius: 0,
                                        minHeight: 200,
                                        padding: 28,
                                    }}
                                >
                                    <div
                                        className="w-20 h-20 rounded-2xl grid place-items-center bg-white shadow-lg"
                                        style={{
                                            boxShadow: "0 12px 30px rgba(30, 58, 95, 0.15)",
                                        }}
                                    >
                                        <i className={`${s.icon} text-3xl`} style={{ color: s.accent }} />
                                    </div>
                                </div>

                                {/* Body */}
                                <div className="p-7 flex-1 flex flex-col">
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">
                                        {s.eyebrow}
                                    </div>
                                    <h3
                                        className="heading-section text-2xl md:text-3xl text-slate-900"
                                    >
                                        {s.title}
                                    </h3>
                                    <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                                        {s.summary}
                                    </p>

                                    {/* Channels */}
                                    {s.channels && (
                                        <div className="mt-5 flex flex-wrap gap-1.5">
                                            {s.channels.slice(0, 4).map((ch) => (
                                                <span
                                                    key={ch}
                                                    className="text-[11px] px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600"
                                                >
                                                    {ch}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* CTA */}
                                    <div className="mt-6 pt-6 border-t border-slate-100 flex items-center justify-between">
                                        <span className="text-sm font-medium text-slate-900 group-hover:text-[color:var(--brand-blue,#2e7d6b)] transition-colors">
                                            Learn more
                                        </span>
                                        <i
                                            className="fa-solid fa-arrow-right text-sm text-slate-400 group-hover:translate-x-1 group-hover:text-[color:var(--brand-blue,#2e7d6b)] transition-all"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        </Link>
                    </StaggerChild>
                ))}
            </StaggerGroup>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   MEET YOUR NEW TEAMMATES — 6-agent grid (Gleap's Kai lineup style)
------------------------------------------------------------------- */
export const MeetTeammates = () => {
    const agents = SOLUTIONS.concat({
        slug: "governance",
        title: "One platform",
        summary:
            "Shared context across bots, SOC 2 compliant. Unlimited seats on Team plan and above.",
        icon: "fa-solid fa-shield-halved",
        accent: "#57534e",
    });

    return (
        <section className="py-24 md:py-28 bg-warm" data-testid="meet-teammates">
            <div className="container-x">
                <Reveal>
                    <div className="mb-16 max-w-2xl">
                        <div className="text-eyebrow text-slate-500 mb-2">Meet your new teammates</div>
                        <h2 className="heading-section text-3xl md:text-4xl">
                            <span className="text-slate-400">Specialist AI agents,</span> each top 1% at one job
                        </h2>
                    </div>
                </Reveal>

                <StaggerGroup className="grid md:grid-cols-3 gap-x-10 gap-y-14">
                    {agents.map((a) => (
                        <StaggerChild key={a.slug}>
                            <div className="flex gap-4">
                                <div
                                    className="w-11 h-11 rounded-xl grid place-items-center shrink-0"
                                    style={{
                                        background: `${a.accent}15`,
                                        color: a.accent,
                                        border: `1px solid ${a.accent}30`,
                                    }}
                                >
                                    <i className={`${a.icon} text-sm`} />
                                </div>
                                <div>
                                    <div className="text-slate-900 font-medium mb-1">{a.title}</div>
                                    <p className="text-slate-500 text-sm leading-relaxed">{a.summary}</p>
                                </div>
                            </div>
                        </StaggerChild>
                    ))}
                </StaggerGroup>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------
   BIG IMPACT CARD — split: gradient/phone-mocks | dark stat + testimonial
------------------------------------------------------------------- */
export const BigImpactCard = () => (
    <section className="py-16 md:py-24 bg-cool" data-testid="big-impact-card">
        <div className="container-x">
            <Reveal>
                <div className="grid md:grid-cols-2 rounded-3xl overflow-hidden shadow-lg">
                    {/* LEFT — mesh gradient with polished chat mockups */}
                    <div className="mesh-frame relative min-h-[440px] flex items-center justify-center p-8 md:p-12" style={{ borderRadius: 0 }}>
                        <div className="relative flex gap-4 items-center">
                            {/* WhatsApp phone */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: 0.1 }}
                                className="device-frame w-44 md:w-52 float-y"
                            >
                                <div className="device-frame-inner p-0 overflow-hidden" style={{ background: "#E5DDD5" }}>
                                    {/* Header */}
                                    <div className="flex items-center gap-2 px-3 py-2 text-white" style={{ background: "#075E54" }}>
                                        <span className="w-6 h-6 rounded-full grid place-items-center text-[9px] font-bold" style={{ background: "#C9F27E", color: "#0E2A23" }}>N</span>
                                        <div className="flex-1">
                                            <div className="text-[10px] font-semibold leading-tight">Nyra Support</div>
                                            <div className="text-[7px] opacity-80 flex items-center gap-1">
                                                <span className="w-1 h-1 rounded-full bg-green-300 inline-block" />
                                                online
                                            </div>
                                        </div>
                                        <i className="fa-solid fa-phone text-[9px] opacity-80" />
                                    </div>
                                    {/* Chat */}
                                    <div className="p-2 space-y-1.5 text-[9px]" style={{ minHeight: 190 }}>
                                        <div className="bg-white rounded-lg rounded-tl-none px-2 py-1.5 text-slate-700 max-w-[85%] shadow-sm">
                                            Hi! Where's my order <b>#4821</b>?
                                        </div>
                                        <div className="ml-auto rounded-lg rounded-tr-none px-2 py-1.5 text-white max-w-[92%] shadow-sm" style={{ background: "#DCF8C6", color: "#0E2A23" }}>
                                            Shipping tomorrow at 9am 📦
                                        </div>
                                        <div className="ml-auto rounded-lg rounded-tr-none px-2 py-1.5 max-w-[92%] shadow-sm" style={{ background: "#DCF8C6", color: "#0E2A23" }}>
                                            <div className="font-semibold text-[9px] mb-0.5">Track live</div>
                                            <div className="flex items-center gap-1 text-[8px] opacity-80">
                                                <i className="fa-solid fa-truck-fast" />
                                                Out for delivery
                                            </div>
                                        </div>
                                        <div className="bg-white rounded-lg rounded-tl-none px-2 py-1.5 text-slate-700 max-w-[85%] shadow-sm">
                                            Any discount for next order?
                                        </div>
                                        <div className="ml-auto rounded-lg rounded-tr-none px-2 py-1.5 max-w-[92%] shadow-sm" style={{ background: "#DCF8C6", color: "#0E2A23" }}>
                                            Yes — code <b>SAVE8</b> is yours 🎉
                                        </div>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 1.6, repeat: Infinity, repeatType: "reverse", duration: 0.8 }}
                                            className="bg-white rounded-lg rounded-tl-none px-2 py-1.5 max-w-[40%] shadow-sm inline-flex items-center gap-1"
                                        >
                                            <span className="w-1 h-1 rounded-full bg-slate-400 inline-block" />
                                            <span className="w-1 h-1 rounded-full bg-slate-400 inline-block" />
                                            <span className="w-1 h-1 rounded-full bg-slate-400 inline-block" />
                                        </motion.div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Web chat phone */}
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                whileInView={{ y: 0, opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: 0.25 }}
                                className="device-frame w-40 md:w-48 float-y"
                                style={{ animationDelay: "1s" }}
                            >
                                <div className="device-frame-inner p-0 overflow-hidden bg-white">
                                    {/* Header */}
                                    <div className="px-3 py-2.5 flex items-center gap-2 text-white" style={{ background: "linear-gradient(135deg, #0E2A23 0%, #235F52 100%)" }}>
                                        <span className="w-6 h-6 rounded-lg grid place-items-center" style={{ background: "rgba(201,242,126,0.2)" }}>
                                            <i className="fa-solid fa-bolt text-[9px]" style={{ color: "#C9F27E" }} />
                                        </span>
                                        <div className="flex-1">
                                            <div className="text-[10px] font-semibold leading-tight">Nyra Agent</div>
                                            <div className="text-[7px] opacity-70">Resolves in seconds</div>
                                        </div>
                                    </div>
                                    <div className="p-2.5 space-y-1.5 text-[9px]" style={{ minHeight: 190 }}>
                                        <div className="text-[8px] text-slate-400 text-center">Today · 14:32</div>
                                        <div className="rounded-lg px-2 py-1.5 max-w-[85%] shadow-sm border border-slate-100 text-slate-700">
                                            Hi Priya 👋 how can I help?
                                        </div>
                                        <div className="grid grid-cols-2 gap-1 mt-1.5">
                                            {["Book appt", "Report issue", "My orders", "Talk human"].map((q) => (
                                                <div key={q} className="text-[8px] rounded-md px-1.5 py-1 text-center font-medium border" style={{ borderColor: "#5FA88A55", color: "#235F52", background: "#5FA88A0D" }}>
                                                    {q}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="ml-auto rounded-lg px-2 py-1.5 max-w-[85%] shadow-sm text-white" style={{ background: "#2E7D6B" }}>
                                            Book appointment
                                        </div>
                                        <div className="rounded-lg px-2 py-1.5 max-w-[92%] shadow-sm border border-slate-100 text-slate-700">
                                            <div className="font-semibold text-[9px] mb-0.5 text-slate-900">Dr Sharma · Tomorrow</div>
                                            <div className="flex items-center gap-1.5 text-[8px] text-slate-500">
                                                <i className="fa-solid fa-circle-check" style={{ color: "#5FA88A" }} />
                                                Confirmed — 10:30am
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* RIGHT — dark stat + testimonial */}
                    <div className="mesh-frame mesh-dark p-10 md:p-14 flex flex-col justify-center" style={{ borderRadius: 0 }}>
                        <div className="text-eyebrow text-slate-300 mb-3">Inbound support handled by Nyra</div>
                        <div className="flex items-baseline gap-3 mb-6">
                            <span className="heading-stat text-6xl md:text-7xl" style={{ color: "#ffffff" }}>
                                50-60%
                            </span>
                            <i className="fa-solid fa-arrow-up text-2xl" style={{ color: "#ffffff" }} />
                        </div>
                        <p className="text-slate-300 leading-relaxed text-sm max-w-md">
                            Multiple enterprise clients run their entire customer conversations on NyraAI — from support and
                            feedback to WhatsApp campaigns — with Nyra resolving half of inbound queries before a human reads them.
                        </p>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
